import { c as _c } from "react/compiler-runtime";
import useAppElement from '@/contexts/appContext/hooks/useAppElement';
import useMutationObservable, { USE_MUTATION_OBSERVABLE_DEFAULT_OPTIONS } from '@/hooks/useMutationObserver';
import { parseCssSize } from '@winelidays/wld-tsx-2/utils';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
const INITIAL_INTERNAL_FORM_ERRORS = {
    firstFieldInError: null,
    showFormErrors: false,
    setShowFormErrors: () => undefined
};
const MUTATION_OBSERVER_OPTIONS = {
    ...USE_MUTATION_OBSERVABLE_DEFAULT_OPTIONS,
    config: {
        ...USE_MUTATION_OBSERVABLE_DEFAULT_OPTIONS.config,
        attributeFilter: ['data-form-field', 'data-form-id', 'data-form-error']
    },
    debounceTime: 100
};
const useInternalFormErrors = () => {
    const $ = _c(14);
    const location = useLocation();
    const appElement = useAppElement();
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t0 = {};
        $[0] = t0;
    }
    else {
        t0 = $[0];
    }
    const [formErrors, setFormErrors] = useState(t0);
    const [loadingFormErrors, setLoadingFormErrors] = useState(true);
    const [scrollToFirstError, setScrollToFirstError] = useState("");
    let t1;
    let t2;
    if ($[1] !== formErrors || $[2] !== scrollToFirstError) {
        t1 = () => {
            if (scrollToFirstError && formErrors[scrollToFirstError]?.firstFieldInError) {
                const modalParent = formErrors[scrollToFirstError].firstFieldInError.closest("[data-modal-content]");
                let top;
                const container = modalParent ?? window;
                const firstErrorRect = formErrors[scrollToFirstError].firstFieldInError.getBoundingClientRect();
                let isVisible;
                if (modalParent) {
                    const containerRect = modalParent.getBoundingClientRect();
                    isVisible =
                        firstErrorRect.top > containerRect.top &&
                            firstErrorRect.top + firstErrorRect.height / 2 <= screen.height - containerRect.top;
                    top = firstErrorRect.top + modalParent.scrollTop - containerRect.top;
                }
                else {
                    isVisible =
                        firstErrorRect.top > 0 && firstErrorRect.top + firstErrorRect.height / 2 <= screen.height;
                    top =
                        firstErrorRect.top -
                            document.body.getBoundingClientRect().top -
                            parseCssSize(window.getComputedStyle(formErrors[scrollToFirstError].firstFieldInError).marginTop);
                }
                if (!isVisible) {
                    container.scrollTo({ top, behavior: "smooth" });
                }
                setScrollToFirstError("");
            }
        };
        t2 = [scrollToFirstError, formErrors];
        $[1] = formErrors;
        $[2] = scrollToFirstError;
        $[3] = t1;
        $[4] = t2;
    }
    else {
        t1 = $[3];
        t2 = $[4];
    }
    useLayoutEffect(t1, t2);
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = () => () => {
            setFormErrors({});
            setLoadingFormErrors(true);
            setScrollToFirstError("");
        };
        $[5] = t3;
    }
    else {
        t3 = $[5];
    }
    let t4;
    if ($[6] !== location.pathname) {
        t4 = [location.pathname];
        $[6] = location.pathname;
        $[7] = t4;
    }
    else {
        t4 = $[7];
    }
    useEffect(t3, t4);
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        const setShowFormErrors = (formId) => (t6) => {
            const val = t6 === undefined ? true : t6;
            setFormErrors((c) => {
                const newFormErrors = { ...c };
                newFormErrors[formId] = { ...newFormErrors[formId], showFormErrors: val };
                return (newFormErrors);
            });
            if (val) {
                setScrollToFirstError(formId);
            }
        };
        const updateFormErrors = (elems) => {
            setFormErrors((prev) => elems.reduce((acc, el) => {
                const formId_0 = el.getAttribute("data-form-id");
                if (formId_0) {
                    if (!acc[formId_0]) {
                        acc[formId_0] = { ...INITIAL_INTERNAL_FORM_ERRORS, setShowFormErrors: setShowFormErrors(formId_0), showFormErrors: !!prev[formId_0]?.showFormErrors };
                    }
                    if (!acc[formId_0].firstFieldInError && el.hasAttribute("data-form-error")) {
                        acc[formId_0].firstFieldInError = el;
                    }
                }
                return (acc);
            }, {}));
            setLoadingFormErrors(false);
        };
        t5 = () => {
            setLoadingFormErrors(true);
            const elements = Array.from(document.querySelectorAll("[data-form-field][data-form-id]"));
            updateFormErrors(elements);
        };
        $[8] = t5;
    }
    else {
        t5 = $[8];
    }
    const handleMutation = t5;
    useMutationObservable(appElement, handleMutation, MUTATION_OBSERVER_OPTIONS);
    let t6;
    if ($[9] !== formErrors || $[10] !== loadingFormErrors) {
        let t7;
        if ($[12] !== loadingFormErrors) {
            t7 = (t8) => {
                const [formId_1, t9] = t8;
                const { firstFieldInError, ...rest } = t9;
                return [
                    formId_1, { isFormInError: !!firstFieldInError, loadingFormErrors, ...rest }
                ];
            };
            $[12] = loadingFormErrors;
            $[13] = t7;
        }
        else {
            t7 = $[13];
        }
        t6 = Object.fromEntries(Object.entries(formErrors).map(t7));
        $[9] = formErrors;
        $[10] = loadingFormErrors;
        $[11] = t6;
    }
    else {
        t6 = $[11];
    }
    return t6;
};
export default useInternalFormErrors;
